import * as Matter from 'matter-js';
import Delaunator from 'delaunator';

export class ExplodingHead {
    private engine: Matter.Engine;
    private world: Matter.World;
    private runner: Matter.Runner;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private headImage: HTMLImageElement;
    private fragments: Matter.Body[] = [];
    private walls: Matter.Body[] = [];
    private scrollOffset: number = 0;
    private headPosition: { x: number, y: number } = { x: 0, y: 0 };
    private startTime: number = 0;
    private opacity: number = 1;
    private debugBodies: boolean = false; // Toggle for debug visualization
    private bloodSplatters: { x: number, y: number, radius: number, opacity: number }[] = [];
    private bloodSpurts: { bodies: Matter.Body[], constraints: Matter.Constraint[] }[] = [];
    
    constructor(canvas: HTMLCanvasElement, headImage: HTMLImageElement) {
        this.canvas = canvas;
        this.ctx = canvas.getContext('2d')!;
        this.headImage = headImage;
        
        // Create Matter.js engine
        this.engine = Matter.Engine.create();
        this.world = this.engine.world;
        
        // Set gravity
        this.engine.gravity.y = 2.0;
        
        // Create runner
        this.runner = Matter.Runner.create();
    }
    
    public explode(containerRect: DOMRect, scrollY: number) {
        this.scrollOffset = scrollY;
        
        // Clear any existing fragments
        this.clearFragments();
        
        // Create walls for boundaries (no side walls for better mobile experience)
        const walls: Matter.Body[] = [];
        
        // Add text element boundaries
        const textBodies = this.createTextBoundaries();
        walls.push(...textBodies);
        
        this.walls = walls;
        Matter.World.add(this.world, walls);
        
        // Calculate head position in viewport coordinates
        const scale = 0.372;
        const headX = containerRect.left + 91;
        const headY = containerRect.top;
        const headWidth = this.headImage.naturalWidth * scale;
        const headHeight = this.headImage.naturalHeight * scale;
        
        // Store head position for texture mapping
        this.headPosition = { x: headX, y: headY };
        
        // Create 16 pieces with physics properties
        const fragmentOptions = {
            restitution: 0.6, // Bounciness
            friction: 0.1,
            density: 0.001,
            render: { fillStyle: 'transparent' }
        };
        
        // Create 4x4 grid
        const gridSize = 4;
        const cellWidth = headWidth / gridSize;
        const cellHeight = headHeight / gridSize;
        
        // Center point for explosion - lower center to send more pieces upward
        const centerX = headX + headWidth * 0.5;
        const centerY = headY + headHeight * 0.60; // Explosion center at 60% down
        
        this.fragments = [];
        
        // Create fragments for two heads
        for (let headNum = 0; headNum < 2; headNum++) {
            for (let row = 0; row < gridSize; row++) {
                for (let col = 0; col < gridSize; col++) {
                    const x = headX + col * cellWidth + cellWidth / 2;
                    const y = headY + row * cellHeight + cellHeight / 2;
                    
                    const fragment = Matter.Bodies.rectangle(
                        x,
                        y,
                        cellWidth * 0.9, // Small gap between pieces
                        cellHeight * 0.9,
                        {
                            ...fragmentOptions,
                            label: `fragment_${headNum}_${row}_${col}`
                        }
                    );
                    
                    // Calculate explosion force based on distance from center
                    const dx = x - centerX;
                    const dy = y - centerY;
                    const distance = Math.sqrt(dx * dx + dy * dy) || 1;
                    const forceScale = 0.06; // Halved for gentler explosion
                    
                    // Apply outward force with variation for each head layer
                    const forceMult = 1.0 + (headNum * 0.05); // Each layer gets slightly more force
                    Matter.Body.applyForce(fragment, fragment.position, { 
                        x: (dx / distance) * forceScale * forceMult,
                        y: (dy / distance) * forceScale * forceMult - 0.05 // Reduced upward bias
                    });
                    
                    // Add random rotation
                    Matter.Body.setAngularVelocity(fragment, (Math.random() - 0.5) * 0.5);
                    
                    this.fragments.push(fragment);
                }
            }
        }
        
        // Add all fragments to world
        Matter.World.add(this.world, this.fragments);
        
        // Create blood splatters
        this.createBloodSplatters(centerX, centerY);
        
        // Create blood spurts
        this.createBloodSpurts(centerX, centerY);
        
        // Start physics
        Matter.Runner.run(this.runner, this.engine);
        
        // Record start time
        this.startTime = Date.now();
        
        // Start rendering
        this.animate();
    }
    
    private animate = () => {
        if (this.fragments.length === 0) return;
        
        // Calculate elapsed time and opacity
        const elapsed = (Date.now() - this.startTime) / 1000; // seconds
        
        // Start fading after 3 seconds, finish at 4 seconds
        if (elapsed > 3) {
            this.opacity = Math.max(0, 1 - (elapsed - 3));
        }
        
        // Clear canvas (transparent)
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Set global alpha for fading
        this.ctx.globalAlpha = this.opacity;
        
        // Update scroll offset first
        const currentScroll = window.scrollY;
        const scrollDelta = currentScroll - this.scrollOffset;
        
        // Draw blood splatters (behind fragments) - adjust for scroll
        this.ctx.save();
        this.ctx.fillStyle = '#8B0000'; // Dark red color
        
        this.bloodSplatters.forEach(splatter => {
            this.ctx.globalAlpha = this.opacity * splatter.opacity;
            this.ctx.beginPath();
            this.ctx.arc(splatter.x, splatter.y, splatter.radius, 0, Math.PI * 2);
            this.ctx.fill();
        });
        this.ctx.restore();
        
        // Draw blood spurts with gluggy appearance
        this.ctx.save();
        
        this.bloodSpurts.forEach((spurt, index) => {
            if (spurt.bodies.length < 1) return;
            
            this.ctx.globalAlpha = this.opacity * 0.8;
            this.ctx.fillStyle = '#8B0000';
            this.ctx.strokeStyle = '#660000';
            this.ctx.lineWidth = 2;
            
            // Draw gluggy blob shape - simplified approach
            const positions = spurt.bodies.map((body: any, j) => {
                // Update the angle (2 degrees per frame)
                const angleIncrement = 2 * Math.PI / 180; // 2 degrees in radians
                body.plugin.sizeAngle = (body.plugin.sizeAngle + angleIncrement) % (Math.PI * 2);
                
                // Calculate dynamic radius based on the particle's angle
                const sineValue = Math.sin(body.plugin.sizeAngle);
                // Map sine output (-1 to 1) to radius range (5 to 15)
                const dynamicRadius = 10 + sineValue * 5;
                
                return {
                    x: body.position.x,
                    y: body.position.y,
                    radius: dynamicRadius
                };
            });
            
            if (positions.length === 1) {
                // Single particle - just draw a circle
                const pos = positions[0];
                this.ctx.beginPath();
                this.ctx.arc(pos.x, pos.y, pos.radius + 2, 0, Math.PI * 2);
                this.ctx.fill();
                this.ctx.stroke();
            } else if (positions.length >= 2) {
                // First, draw connecting shapes between particles
                for (let i = 0; i < positions.length - 1; i++) {
                    const curr = positions[i];
                    const next = positions[i + 1];
                    const angle = Math.atan2(next.y - curr.y, next.x - curr.x);
                    const distance = Math.sqrt((next.x - curr.x) ** 2 + (next.y - curr.y) ** 2);
                    
                    // Draw a stretched capsule shape between particles
                    this.ctx.beginPath();
                    
                    // Top arc of current particle
                    this.ctx.arc(curr.x, curr.y, curr.radius + 2, angle + Math.PI/2, angle - Math.PI/2, true);
                    
                    // Top line to next particle
                    this.ctx.lineTo(
                        next.x + Math.cos(angle + Math.PI/2) * (next.radius + 2),
                        next.y + Math.sin(angle + Math.PI/2) * (next.radius + 2)
                    );
                    
                    // Arc around next particle
                    this.ctx.arc(next.x, next.y, next.radius + 2, angle + Math.PI/2, angle - Math.PI/2, false);
                    
                    // Bottom line back
                    this.ctx.lineTo(
                        curr.x + Math.cos(angle - Math.PI/2) * (curr.radius + 2),
                        curr.y + Math.sin(angle - Math.PI/2) * (curr.radius + 2)
                    );
                    
                    this.ctx.closePath();
                    this.ctx.fill();
                }
                
                // Then draw circles at each particle position to smooth joints
                positions.forEach(pos => {
                    this.ctx.beginPath();
                    this.ctx.arc(pos.x, pos.y, pos.radius + 2, 0, Math.PI * 2);
                    this.ctx.fill();
                });
                
                // Finally, add stroke for definition
                this.ctx.lineWidth = 2;
                positions.forEach(pos => {
                    this.ctx.beginPath();
                    this.ctx.arc(pos.x, pos.y, pos.radius + 2, 0, Math.PI * 2);
                    this.ctx.stroke();
                });
            }
        });
        this.ctx.restore();
        
        // Reset global alpha for fragments
        this.ctx.globalAlpha = this.opacity;
        
        if (scrollDelta !== 0) {
            // Update all fragment positions to follow scroll
            this.fragments.forEach(fragment => {
                Matter.Body.setPosition(fragment, {
                    x: fragment.position.x,
                    y: fragment.position.y - scrollDelta
                });
            });
            
            // Update blood splatter positions to follow scroll
            this.bloodSplatters.forEach(splatter => {
                splatter.y = splatter.y - scrollDelta;
            });
            
            // Update blood spurt positions to follow scroll
            this.bloodSpurts.forEach(spurt => {
                spurt.bodies.forEach(body => {
                    Matter.Body.setPosition(body, {
                        x: body.position.x,
                        y: body.position.y - scrollDelta
                    });
                });
            });
            
            // Update text boundaries too
            const newTextBodies = this.createTextBoundaries();
            
            // Remove old text bodies (skip the wall bodies)
            const wallsToKeep = this.walls.filter(wall => 
                wall.label === 'leftWall' || wall.label === 'rightWall'
            );
            
            // Remove all old bodies
            Matter.World.remove(this.world, this.walls);
            
            // Add walls back and new text bodies
            this.walls = [...wallsToKeep, ...newTextBodies];
            Matter.World.add(this.world, this.walls);
            
            this.scrollOffset = currentScroll;
        }
        
        // Draw fragments with textures
        this.fragments.forEach((fragment, index) => {
            this.ctx.save();
            
            // Translate to fragment position
            this.ctx.translate(fragment.position.x, fragment.position.y);
            this.ctx.rotate(fragment.angle);
            
            // Draw the appropriate piece of the image
            const scale = 0.372;
            const imgWidth = this.headImage.naturalWidth;
            const imgHeight = this.headImage.naturalHeight;
            const gridSize = 4;
            const cellWidth = imgWidth / gridSize;
            const cellHeight = imgHeight / gridSize;
            
            // Extract row and col from label (fragment_headNum_row_col)
            const parts = fragment.label.split('_');
            const row = parseInt(parts[2]);
            const col = parseInt(parts[3]);
            
            const sourceX = col * cellWidth;
            const sourceY = row * cellHeight;
            
            // Draw the fragment piece
            this.ctx.drawImage(
                this.headImage,
                sourceX, sourceY, cellWidth, cellHeight, // Source
                -cellWidth * scale * 0.5, -cellHeight * scale * 0.5, // Destination
                cellWidth * scale, cellHeight * scale
            );
            
            this.ctx.restore();
        });
        
        // Debug: Draw boundaries
        if (this.debugBodies) {
            this.ctx.save();
            this.ctx.globalAlpha = 1; // Full opacity for debug
            this.ctx.strokeStyle = 'red';
            this.ctx.lineWidth = 2;
            
            this.walls.forEach(wall => {
                // Draw text boundaries
                const vertices = wall.vertices;
                this.ctx.beginPath();
                vertices.forEach((vertex, i) => {
                    if (i === 0) {
                        this.ctx.moveTo(vertex.x, vertex.y);
                    } else {
                        this.ctx.lineTo(vertex.x, vertex.y);
                    }
                });
                this.ctx.closePath();
                this.ctx.stroke();
                
                // Label the boundary
                this.ctx.fillStyle = 'red';
                this.ctx.font = '12px Arial';
                this.ctx.fillText(wall.label || '', wall.position.x, wall.position.y);
            });
            
            this.ctx.restore();
        }
        
        requestAnimationFrame(this.animate);
    }
    
    public stop() {
        Matter.Runner.stop(this.runner);
        this.clearFragments();
    }
    
    private clearFragments() {
        if (this.fragments.length > 0) {
            Matter.World.remove(this.world, this.fragments);
            this.fragments = [];
        }
        if (this.walls.length > 0) {
            Matter.World.remove(this.world, this.walls);
            this.walls = [];
        }
        this.bloodSplatters = [];
        
        // Clear blood spurts
        this.bloodSpurts.forEach(spurt => {
            Matter.World.remove(this.world, spurt.bodies);
            Matter.World.remove(this.world, spurt.constraints);
        });
        this.bloodSpurts = [];
    }
    
    private createBloodSplatters(centerX: number, centerY: number) {
        const splatterCount = 50;
        this.bloodSplatters = [];
        
        for (let i = 0; i < splatterCount; i++) {
            // Use Box-Muller transform for normal distribution
            let u1 = Math.random();
            let u2 = Math.random();
            let z0 = Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
            let z1 = Math.sqrt(-2 * Math.log(u1)) * Math.sin(2 * Math.PI * u2);
            
            // Scale the distribution - wider spread
            const distanceFromCenter = Math.abs(z0) * 450; // 3x wider spread (was 150px)
            const angle = Math.random() * Math.PI * 2;
            
            // Position based on normal distribution
            const x = centerX + Math.cos(angle) * distanceFromCenter;
            const y = centerY + Math.sin(angle) * distanceFromCenter;
            
            // Size with steeper falloff - many more small ones
            const maxSize = 25; // Halved from 50
            const minSize = 5;
            // Use power function for steeper dropoff
            const normalizedDistance = Math.min(distanceFromCenter / 300, 1); // Normalize to 0-1
            const sizeFactor = Math.pow(1 - normalizedDistance, 3); // Cubic falloff
            const radius = minSize + (maxSize - minSize) * sizeFactor;
            
            // Opacity inversely proportional to size - bigger = more transparent
            const sizeRatio = (radius - minSize) / (maxSize - minSize); // 0 to 1
            const baseOpacity = 0.7 - (sizeRatio * 0.5); // 0.7 for smallest, 0.2 for largest
            const opacity = baseOpacity + (Math.random() * 0.1 - 0.05); // Add small random variation
            
            this.bloodSplatters.push({ x, y, radius, opacity });
        }
    }
    
    private createBloodSpurts(centerX: number, centerY: number) {
        const spurtCount = 5; // Number of blood spurts
        this.bloodSpurts = [];
        
        for (let i = 0; i < spurtCount; i++) {
            const bodies: Matter.Body[] = [];
            const constraints: Matter.Constraint[] = [];
            
            // Random angle for spurt direction - mostly upward
            const angle = Math.PI * 0.3 + (Math.random() * Math.PI * 0.4); // 54-126 degrees (upward arc)
            const force = 0.15 + Math.random() * 0.1; // Vary the force
            
            // Create 1-3 connected bodies per spurt (evenly distributed)
            const particleCount = 1 + (i % 3); // This gives us 1, 2, 3, 1, 2 pattern
            const baseRadius = 10; // Base radius for calculations
            const spacing = baseRadius * 3; // More spacing for stretch
            
            for (let j = 0; j < particleCount; j++) {
                const x = centerX + Math.cos(angle) * j * spacing;
                const y = centerY + Math.sin(angle) * j * spacing;
                
                // Initial particle radius (will oscillate during animation)
                const particleRadius = 10;
                
                const body = Matter.Bodies.circle(x, y, particleRadius, {
                    restitution: 0.3,
                    friction: 0.1,
                    density: 0.002,
                    label: `bloodSpurt_${i}_${j}`,
                    render: { fillStyle: 'transparent' },
                    collisionFilter: {
                        category: 0x0002, // Separate collision category for blood
                        mask: 0x0000 // Don't collide with anything
                    },
                    // Store custom data on the body
                    plugin: {
                        sizeAngle: Math.random() * Math.PI * 2 // Random starting angle for size oscillation
                    }
                } as any);
                
                // Apply initial velocity
                Matter.Body.setVelocity(body, {
                    x: Math.cos(angle) * force * 100 * (1 - j * 0.2), // Reduce force for trailing particles
                    y: Math.sin(angle) * force * 100 * (1 - j * 0.2) * -1 // Negative for upward
                });
                
                bodies.push(body);
                
                // Create spring constraint to previous particle
                if (j > 0) {
                    const constraint = Matter.Constraint.create({
                        bodyA: bodies[j - 1],
                        bodyB: body,
                        length: spacing,
                        stiffness: 0.02, // Even looser for maximum stretch
                        damping: 0.01, // Almost no damping
                        render: { visible: false }
                    });
                    constraints.push(constraint);
                }
            }
            
            // Add to physics world
            Matter.World.add(this.world, bodies);
            Matter.World.add(this.world, constraints);
            
            this.bloodSpurts.push({ bodies, constraints });
        }
    }
    
    private createTextBoundaries(): Matter.Body[] {
        const bodies: Matter.Body[] = [];
        
        // Get the hero section elements
        const h1 = document.querySelector('#hero h1');
        const h2 = document.querySelector('#hero h2');
        const synopsis = document.querySelector('#hero .synopsis p'); // Get the actual paragraph
        const buttons = document.querySelectorAll('#hero .social-links .btn');
        
        // Create individual letter boundaries for "Paul Kelcey"
        if (h1) {
            const rect = h1.getBoundingClientRect();
            const style = window.getComputedStyle(h1);
            const text = h1.textContent || '';
            
            // Set up canvas context for text measurement
            this.ctx.save();
            this.ctx.font = style.font;
            this.ctx.textAlign = 'left';
            this.ctx.textBaseline = 'alphabetic';
            
            let currentX = rect.left;
            
            // Get font metrics for better height calculation
            const fontSize = parseFloat(style.fontSize);
            
            // Measure each letter
            for (let i = 0; i < text.length; i++) {
                const letter = text[i];
                if (letter === ' ') {
                    // Skip spaces but advance position
                    const spaceWidth = this.ctx.measureText(' ').width;
                    currentX += spaceWidth;
                    continue;
                }
                
                const metrics = this.ctx.measureText(letter);
                const letterWidth = metrics.width;
                
                // Use actual font metrics for height
                const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
                const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
                const letterHeight = ascent + descent;
                
                // Calculate vertical position with letter-specific adjustments
                const elementCenterY = rect.top + rect.height / 2;
                
                // Letter-specific Y offsets to perfectly align boundaries
                // Positive values move down, negative move up
                const letterOffsets: {[key: string]: number} = {
                    'P': 0.05,
                    'a': 0.08,
                    'u': 0.08,
                    'l': 0.05,
                    'K': 0.05,
                    'e': 0.08,
                    'c': 0.08,
                    'y': 0.12,  // Has descender, needs more downward offset
                };
                
                const offsetRatio = letterOffsets[letter] || 0.08;
                const letterCenterY = elementCenterY + rect.height * offsetRatio;
                
                bodies.push(Matter.Bodies.rectangle(
                    currentX + letterWidth / 2,
                    letterCenterY,
                    letterWidth,
                    letterHeight,
                    { isStatic: true, label: `letter_${letter}` }
                ));
                
                currentX += letterWidth;
            }
            
            this.ctx.restore();
        }
        
        // Create single boundary for "Technology Leadership"
        if (h2) {
            const rect = h2.getBoundingClientRect();
            const style = window.getComputedStyle(h2);
            const text = h2.textContent || '';
            
            // Measure the full text with proper metrics
            this.ctx.save();
            this.ctx.font = style.font;
            this.ctx.textAlign = 'center';
            this.ctx.textBaseline = 'alphabetic';
            
            const metrics = this.ctx.measureText(text);
            const textWidth = metrics.width;
            
            // Use actual bounding box metrics
            const fontSize = parseFloat(style.fontSize);
            const ascent = metrics.actualBoundingBoxAscent || fontSize * 0.8;
            const descent = metrics.actualBoundingBoxDescent || fontSize * 0.2;
            const textHeight = ascent + descent;
            
            this.ctx.restore();
            
            // For left-aligned text, position boundary at the actual text location
            // The text starts at the left edge of the element
            const textCenterX = rect.left + textWidth / 2;
            
            bodies.push(Matter.Bodies.rectangle(
                textCenterX,
                rect.top + rect.height / 2,
                textWidth,
                textHeight,
                { isStatic: true, label: 'h2Text' }
            ));
        }
        
        if (synopsis) {
            const rect = synopsis.getBoundingClientRect();
            // For paragraph, use the actual content height
            bodies.push(Matter.Bodies.rectangle(
                rect.left + rect.width / 2,
                rect.top + rect.height / 2,
                rect.width,
                rect.height,
                { isStatic: true, label: 'synopsis' }
            ));
        }
        
        // Create individual bodies for each button
        buttons.forEach((button, index) => {
            const rect = button.getBoundingClientRect();
            bodies.push(Matter.Bodies.rectangle(
                rect.left + rect.width / 2,
                rect.top + rect.height / 2,
                rect.width + 10, // Small padding
                rect.height + 5,
                { isStatic: true, label: `button${index}` }
            ));
        });
        
        return bodies;
    }
    
    private getHeadOutlinePoints() {
        return [
            {x: 0.5, y: 0.05},   // Top of head
            {x: 0.75, y: 0.08},  // Top right
            {x: 0.9, y: 0.2},    // Upper temple right
            {x: 0.95, y: 0.35},  // Temple right
            {x: 0.98, y: 0.45},  // Ear top right
            {x: 0.98, y: 0.55},  // Ear bottom right
            {x: 0.9, y: 0.65},   // Below ear right
            {x: 0.75, y: 0.85},  // Jaw right
            {x: 0.6, y: 0.95},   // Chin right
            {x: 0.4, y: 0.95},   // Chin left
            {x: 0.25, y: 0.85},  // Jaw left
            {x: 0.1, y: 0.65},   // Below ear left
            {x: 0.02, y: 0.55},  // Ear bottom left
            {x: 0.02, y: 0.45},  // Ear top left
            {x: 0.05, y: 0.35},  // Temple left
            {x: 0.1, y: 0.2},    // Upper temple left
            {x: 0.25, y: 0.08},  // Top left
        ];
    }
    
    private drawHeadOutline(containerRect: DOMRect) {
        // Calculate head position and size
        const scale = 0.372;
        const headX = containerRect.left + 91;
        const headY = containerRect.top;
        const headWidth = this.headImage.naturalWidth * scale;
        const headHeight = this.headImage.naturalHeight * scale;
        
        // Head outline points (normalized 0-1)
        const outlinePoints = [
            {x: 0.5, y: 0.05},   // Top of head
            {x: 0.75, y: 0.08},  // Top right
            {x: 0.9, y: 0.2},    // Upper temple right
            {x: 0.95, y: 0.35},  // Temple right
            {x: 0.98, y: 0.45},  // Ear top right
            {x: 0.98, y: 0.55},  // Ear bottom right
            {x: 0.9, y: 0.65},   // Below ear right
            {x: 0.75, y: 0.85},  // Jaw right
            {x: 0.6, y: 0.95},   // Chin right
            {x: 0.4, y: 0.95},   // Chin left
            {x: 0.25, y: 0.85},  // Jaw left
            {x: 0.1, y: 0.65},   // Below ear left
            {x: 0.02, y: 0.55},  // Ear bottom left
            {x: 0.02, y: 0.45},  // Ear top left
            {x: 0.05, y: 0.35},  // Temple left
            {x: 0.1, y: 0.2},    // Upper temple left
            {x: 0.25, y: 0.08},  // Top left
        ];
        
        // Draw the outline
        this.ctx.strokeStyle = 'red';
        this.ctx.lineWidth = 2;
        this.ctx.beginPath();
        
        outlinePoints.forEach((point, index) => {
            const x = headX + point.x * headWidth;
            const y = headY + point.y * headHeight;
            
            if (index === 0) {
                this.ctx.moveTo(x, y);
            } else {
                this.ctx.lineTo(x, y);
            }
        });
        
        this.ctx.closePath();
        this.ctx.stroke();
        
        // Also draw the head image faintly for reference
        this.ctx.globalAlpha = 0.3;
        this.ctx.drawImage(
            this.headImage,
            0, 0, this.headImage.naturalWidth, this.headImage.naturalHeight,
            headX, headY, headWidth, headHeight
        );
        this.ctx.globalAlpha = 1.0;
    }
}